import { type FieldError, type FieldErrors, type FieldValues } from 'react-hook-form';

import { type FlowListName } from '@/components/Wizard';

import {
  CONTINUATION_STEPS,
  EXCLUDED_FLOW_LISTS,
  EXCLUDED_STEPS,
  FLOW_LIST_TO_GTM_FLOW,
  type GtmFlow,
  UNDEFINED_FLOW_STEPS
} from './constants';

const STEP_SUFFIXES = ['-natural', '-partner', '-representative'] as const;

export type TrackedStepContext = {
  stepName: string;
  stepNumber: number;
  flow: GtmFlow | undefined;
};

export type StepNumberRegistry = {
  legalEntityRelation?: unknown;
};

let lastTrackedStep: TrackedStepContext | undefined;

export const getLastTrackedStep = () => lastTrackedStep;

export const setLastTrackedStep = (context: TrackedStepContext) => {
  lastTrackedStep = context;
};

export const isExcludedStep = (stepName: string, flowList?: FlowListName) => {
  if (EXCLUDED_STEPS.has(stepName)) {
    return true;
  }

  return Boolean(flowList && EXCLUDED_FLOW_LISTS.has(flowList));
};

export const toCanonicalStepName = (stepName: string) => {
  for (const suffix of STEP_SUFFIXES) {
    if (!stepName.endsWith(suffix)) {
      continue;
    }

    return stepName.slice(0, -suffix.length);
  }

  return stepName;
};

export const resolveGtmFlow = (
  stepName: string,
  currentFlowList: FlowListName | undefined,
  flowlistHistory: ReadonlySet<FlowListName> | undefined
): GtmFlow | undefined => {
  if (UNDEFINED_FLOW_STEPS.has(stepName)) {
    return undefined;
  }

  const mappedFromCurrent = currentFlowList ? FLOW_LIST_TO_GTM_FLOW[currentFlowList] : undefined;

  if (mappedFromCurrent) {
    return mappedFromCurrent;
  }

  if (!flowlistHistory?.size) {
    return undefined;
  }

  for (const flowList of [...flowlistHistory].reverse()) {
    const mapped = FLOW_LIST_TO_GTM_FLOW[flowList];

    if (!mapped) {
      continue;
    }

    return mapped;
  }

  return undefined;
};

const resolveContinuationStepNumber = (stepName: string): number | undefined => {
  if (!CONTINUATION_STEPS.has(stepName)) {
    return undefined;
  }

  const last = getLastTrackedStep();

  if (!last) {
    return undefined;
  }

  if (last.stepName === stepName) {
    return last.stepNumber;
  }

  return last.stepNumber + 1;
};

export const resolveStepNumber = (
  flowIndex: number,
  flow: GtmFlow | undefined,
  stepName: string,
  flowlistHistory: ReadonlySet<FlowListName> | undefined,
  registry?: StepNumberRegistry
) => {
  if (stepName === 'document-not-found-capture') {
    return 0;
  }

  const continuationStepNumber = resolveContinuationStepNumber(stepName);

  if (continuationStepNumber !== undefined) {
    return continuationStepNumber;
  }

  const documentNotFoundOffset = flowlistHistory?.has('document-not-found') ? 1 : 0;

  if (stepName === 'legal-entity-relation') {
    return documentNotFoundOffset;
  }

  const passedRelation =
    Boolean(registry?.legalEntityRelation) ||
    Boolean(flowlistHistory?.has('legal-entity')) ||
    flow === 'pj_socio' ||
    flow === 'pj_procurador';

  const relationOffset = passedRelation ? 1 : 0;

  return flowIndex + documentNotFoundOffset + relationOffset;
};

export const isEmptyFieldValue = (value: unknown): boolean => {
  if (value === undefined || value === null || value === false) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
};

export const getValueAtPath = (values: FieldValues, path: string): unknown => {
  const segments = path.split('.');
  let current: unknown = values;

  for (const segment of segments) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }

    current = (current as Record<string, unknown>)[segment];
  }

  return current;
};

export const mapFieldErrorType = (error: FieldError, value: unknown): 'campo_obrigatorio' | 'formato_invalido' => {
  if (error.type === 'server') {
    return 'formato_invalido';
  }

  if (isEmptyFieldValue(value)) {
    return 'campo_obrigatorio';
  }

  return 'formato_invalido';
};

export const flattenFieldErrors = (
  errors: FieldErrors<FieldValues>,
  prefix = ''
): Array<{ fieldName: string; error: FieldError }> => {
  const result: Array<{ fieldName: string; error: FieldError }> = [];

  for (const [key, value] of Object.entries(errors)) {
    if (!value || typeof value !== 'object') {
      continue;
    }

    const path = prefix ? `${prefix}.${key}` : key;

    if ('type' in value && typeof value.type === 'string') {
      result.push({ fieldName: path, error: value as FieldError });
      continue;
    }

    result.push(...flattenFieldErrors(value as FieldErrors<FieldValues>, path));
  }

  return result;
};

type BankMappedError = {
  errorCode?: string;
  message?: string;
};

const resolveBankErrorTypes = (errors: BankMappedError[]): string[] => {
  const types: string[] = [];

  for (const item of errors) {
    const type = item.message || item.errorCode;

    if (!type) {
      continue;
    }

    types.push(type);
  }

  return types;
};

const getErrorCodes = (error: object): string[] => {
  if (!('errorCodes' in error)) {
    return [];
  }

  return ((error as { errorCodes?: string[] }).errorCodes ?? []).filter(Boolean);
};

const getBankErrorTypes = (error: object): string[] => {
  if (!('errors' in error)) {
    return [];
  }

  const bankErrors = (error as { errors?: BankMappedError[] }).errors;

  if (!Array.isArray(bankErrors) || !bankErrors.length) {
    return [];
  }

  return resolveBankErrorTypes(bankErrors);
};

export const resolveValidationErrorTypes = (error: unknown, fallbackSubtitle?: unknown): string[] => {
  const objectError = error && typeof error === 'object' ? error : undefined;
  const codes = objectError ? getErrorCodes(objectError) : [];

  if (codes.length) {
    return codes;
  }

  const bankTypes = objectError ? getBankErrorTypes(objectError) : [];

  if (bankTypes.length) {
    return bankTypes;
  }

  if (error instanceof Error && error.message) {
    return [error.message];
  }

  if (typeof fallbackSubtitle === 'string' && fallbackSubtitle) {
    return [fallbackSubtitle];
  }

  return ['unknown_validation_error'];
};

export const buildStepTrackingContext = (
  stepName: string,
  currentFlowList: FlowListName | undefined,
  flowlistHistory: ReadonlySet<FlowListName> | undefined,
  flowIndex: number,
  registry?: StepNumberRegistry
): TrackedStepContext | null => {
  if (isExcludedStep(stepName, currentFlowList)) {
    return null;
  }

  const canonicalName = toCanonicalStepName(stepName);
  const flow = resolveGtmFlow(stepName, currentFlowList, flowlistHistory);
  const stepNumber = resolveStepNumber(flowIndex, flow, stepName, flowlistHistory, registry);

  return {
    stepName: canonicalName,
    stepNumber,
    flow
  };
};
